import earcut from "earcut";
import * as twgl from "twgl.js";
import { findSceneNode, getAllSceneNodes, type CurvaDocument } from "@/document/model/document";
import type { GuideOrientation } from "@/document/model/guides";
import type { PrimitiveNode, SceneNode } from "@/document/model/nodes";
import type { PrimitiveKind } from "@/document/model/primitives";
import type { VectorNetwork } from "@/document/model/vectorNetwork";
import { createDefaultStyle } from "@/document/model/styles";
import { primitiveToPolygon, primitiveToTriangles } from "@/document/operations/primitiveGeometry";
import { getVectorNetworkBounds } from "@/document/operations/vectorGeometry";
import type { Camera } from "@/renderer/camera";
import type { ResizeHandle, RotationHandle } from "@/lib/math/selectionHandles";
import { getResizeHandlePositions } from "@/lib/math/selectionHandles";
import {
  nodeHasCompositeSelectionRotation,
  nodeSelectionCorners,
  nodeSelectionLocalBounds,
  nodeSelectionLocalToWorldMatrix,
  nodeSelectionPointToWorld,
  type ScenePreview,
} from "@/document/operations/nodeWorldTransform";
import type { MeasureOverlay } from "@/editor/tools/measureOverlay";
import { rectCenter, rectCorners, rotatePoint, transformRotationOrigin } from "@/lib/math/rotation";
import { createTriangleBuffer } from "@/renderer/gl/buffers";
import type { DisplayClip } from "@/renderer/scene/displayList";
import {
  affineEquals,
  applyAffinePoint,
  IDENTITY_AFFINE,
  type Affine2D,
} from "@/lib/math/affine";
import type { Rect } from "@/lib/math/rect";
import {
  transformDisplayPoint,
  type PreparedSceneFrame,
  type VectorNetworkPreview,
} from "@/renderer/scene/prepareSceneFrame";
import { caretRectForTextIndex, selectionRectsForTextRange } from "@/renderer/text/canvasKitText";
import {
  BEZIER_HANDLE_RADIUS_PX,
  CIRCLE_STROKE_PX,
  FRAME_LABEL_HEIGHT_PX,
  FRAME_LABEL_PADDING_X_PX,
  GIZMO_BLUE,
  GIZMO_HOVER_DELTA_PX,
  GIZMO_STROKE_PX,
  GIZMO_WHITE,
  PATH_PREVIEW_RADIUS_PX,
  SELECTED_BEZIER_HANDLE_RADIUS_PX,
  SELECTED_VERTEX_RADIUS_PX,
  SELECTION_HANDLE_SIZE_PX,
  SNAP_GUIDE_COLOR,
  VECTOR_EDIT_HOVERED_EDGE_COLOR,
  VECTOR_EDIT_SELECTED_EDGE_COLOR,
  VERTEX_RADIUS_PX,
  adaptiveVectorEditStepsForEdge,
  boundsFromPoints,
  centeredRect,
  circleRingVertices,
  circleVertices,
  clampScreenPointToInsets,
  documentPointToScreen,
  draftPrimitive,
  drawTextWithLetterSpacing,
  ellipsizeText,
  formatCacheNumber,
  pointsToLineSegments,
  rectVertices,
  roundedRectPath,
  sampleVectorEdge,
  sampleVectorEdgeFromMap,
  screenPointToDocument,
  type ViewportScreenInsets,
} from "@/renderer/renderHelpers";
import { boundsFromPoints as boundsFromLabelPoints, FRAME_LABEL_SCRIPT_ACTIONS_WIDTH_PX, frameLabelGeometryForCorners, shouldShowFrameLabels } from "@/renderer/frameLabelGeometry";
import type {
  DocumentGuideOverlay,
  FrameLabelOverlay,
  GradientGizmoOverlay,
  GradientGizmoStopOverlay,
  GuidePreviewOverlay,
  ImageFillGizmoOverlay,
  ObjectSnapGuideOverlay,
  PrimitiveGizmoOverlay,
  PrototypeAnchorOverlay,
  PrototypeLinkOverlay,
  RenderOverlay,
  RenderPreview,
  RotationOriginGizmoOverlay,
  SelectionOverlay,
  TextSelectionOverlay,
  VectorSmartTransformOverlay,
} from "@/renderer/overlayTypes";

export interface InteractiveOverlayHost {
  gl: WebGL2RenderingContext;
  programInfo: twgl.ProgramInfo;
  ellipseProgramInfo: twgl.ProgramInfo;
  textureProgramInfo: twgl.ProgramInfo;
  camera: Camera;
  canvas: HTMLCanvasElement;
  renderCameraOverride?: Camera;
  renderResolutionOverride?: [number, number];
  dimensionLabelTextureCache: Map<string, { texture: WebGLTexture; width: number; height: number }>;
  frameLabelTextureCache: Map<string, WebGLTexture>;
  texturedQuadBufferCache: Map<string, twgl.BufferInfo>;
  vectorEditCache: WeakMap<VectorNetwork, { edgeVertices: number[]; edgeZoom: number }>;
  baseUniforms(positionOffset?: { x: number; y: number }): Record<string, unknown>;
  uniforms(
    color: [number, number, number, number],
    positionOffset?: { x: number; y: number },
    clip?: DisplayClip,
  ): Record<string, unknown>;
  uploadCanvasTexture(canvas: HTMLCanvasElement): WebGLTexture | undefined;
  drawCachedWebGLTexture(
    texture: WebGLTexture,
    bounds: RenderPreview["bounds"] & { corners?: Array<{ x: number; y: number }> },
  ): void;
  viewportDocumentBounds(): RenderPreview["bounds"];
  activeViewportDocumentBounds(): RenderPreview["bounds"];
  recordLabelRaster(): void;
  recordLabelCacheHit(): void;
  displayTriangleBuffer(vertices: Float32Array | number[]): twgl.BufferInfo;
}

export class InteractiveOverlayDrawer {
  constructor(private readonly host: InteractiveOverlayHost) {}

  draw(document: CurvaDocument, overlay: RenderOverlay, sceneFrame: PreparedSceneFrame): void {
    this.host.gl.disable(this.host.gl.SCISSOR_TEST);
    this.host.gl.useProgram(this.host.programInfo.program);

    const vectorPreview = sceneFrame.vectorPreview;

    if (overlay.draftShape) {
      this.drawDraftShape(overlay.draftShape.kind, overlay.draftShape.bounds);
      this.drawDimensionLabel(overlay.draftShape.bounds);
    }

    if (overlay.objectSnapGuides?.length) {
      this.drawObjectSnapGuides(overlay.objectSnapGuides);
    }

    if (overlay.documentGuides?.length || overlay.guidePreview) {
      this.drawDocumentGuides(overlay.documentGuides ?? [], overlay.guidePreview);
    }

    if (overlay.prototypeLinks?.length || overlay.prototypeLinkPreview) {
      this.drawPrototypeLinks(overlay.prototypeLinks ?? [], overlay.prototypeLinkPreview);
    }

    if (!overlay.vectorEditNodeId && (!overlay.measureOverlay || overlay.devMode)) {
      const selectedIds = new Set(overlay.selectedNodeIds);
      const selectionBoundsById = overlay.selectionNodeBoundsById ?? {};
      for (const previewNodeId of overlay.previewSelectedNodeIds ?? []) {
        if (selectedIds.has(previewNodeId)) {
          continue;
        }

        const previewBounds = selectionBoundsById[previewNodeId];
        if (previewBounds) {
          this.drawOrientedSelectionOutline(document, overlay, previewNodeId, previewBounds, [0.56, 0.78, 1, 0.7]);
        }
      }

      if (overlay.hoveredNodeId && !selectedIds.has(overlay.hoveredNodeId)) {
        const hoveredBounds = selectionBoundsById[overlay.hoveredNodeId];
        if (hoveredBounds) {
          this.drawOrientedSelectionOutline(document, overlay, overlay.hoveredNodeId, hoveredBounds, [0.56, 0.78, 1, 0.85]);
        }
      }

      for (const selectedNodeId of overlay.selectedNodeIds) {
        const selectedBounds = selectionBoundsById[selectedNodeId];
        if (selectedBounds) {
          this.drawOrientedSelectionOutline(document, overlay, selectedNodeId, selectedBounds, [0.125, 0.749, 1, 1]);
        }
      }

      if (overlay.selectionBounds && overlay.selectedNodeIds.length > 1) {
        this.drawSelectionOutline(overlay.selectionBounds);
        if (!overlay.devMode) {
          this.drawSelectionHandles(overlay.selectionBounds, overlay.hoveredResizeHandle);
        }
        this.drawDimensionLabel(overlay.selectionBounds);
      } else {
        const primaryNodeId = overlay.selectedNodeIds[0];
        const primaryBounds = primaryNodeId ? selectionBoundsById[primaryNodeId] : undefined;
        if (primaryBounds && primaryNodeId) {
          if (!overlay.devMode) {
            this.drawOrientedSelectionHandles(document, overlay, primaryNodeId, primaryBounds, overlay.hoveredResizeHandle);
          }
          this.drawDimensionLabel(primaryBounds);
        }
      }
    }

    if (overlay.measureOverlay) {
      this.drawMeasureOverlay(overlay.measureOverlay, overlay.visibleViewportInsets);
    }

    if (overlay.marquee) {
      this.drawSelectionOutline(overlay.marquee);
    }

    if (overlay.lasso) {
      this.drawLassoOverlay(overlay.lasso.points);
    }

    if (overlay.vectorEditNodeId) {
      this.drawVectorEditOverlay(document, overlay, vectorPreview);
    }

    if (overlay.vectorMeasureOverlay) {
      this.drawMeasureOverlay(overlay.vectorMeasureOverlay, overlay.visibleViewportInsets);
    }

    if (overlay.primitiveGizmo && !overlay.vectorEditNodeId) {
      this.drawPrimitiveGizmo(overlay.primitiveGizmo);
    }

    if (overlay.gradientGizmo && !overlay.vectorEditNodeId) {
      this.drawGradientGizmo(overlay.gradientGizmo);
    }

    if (overlay.imageFillGizmo && !overlay.vectorEditNodeId) {
      this.drawImageFillGizmo(overlay.imageFillGizmo);
    }

    if (overlay.rotationOriginGizmo && !overlay.vectorEditNodeId) {
      this.drawRotationOriginGizmo(overlay.rotationOriginGizmo);
    }

    if (overlay.prototypeAnchors?.length && !overlay.vectorEditNodeId) {
      this.drawPrototypeAnchors(overlay.prototypeAnchors);
    }

    if (overlay.textSelection) {
      this.drawTextSelectionOverlay(document, overlay.textSelection);
    }

    if (overlay.showPixelGrid && this.host.camera.zoom >= 4) {
      this.drawPixelGrid();
    }

    if (overlay.frameLabels?.length && shouldShowFrameLabels(this.host.camera.zoom)) {
      this.drawFrameLabels(overlay.frameLabels, overlay.hoveredFrameLabelId, new Set(overlay.selectedNodeIds));
    }
  }
  private drawTextSelectionOverlay(document: CurvaDocument, selection: TextSelectionOverlay): void {
    const node = getAllSceneNodes(document).find((candidate): candidate is Extract<SceneNode, { type: "text" }> => candidate.type === "text" && candidate.id === selection.nodeId);
    if (!node) {
      return;
    }

    const zoomBucket = Math.round(this.host.camera.zoom * 10);
    const rangeStart = Math.min(selection.anchor, selection.focus);
    const rangeEnd = Math.max(selection.anchor, selection.focus);
    const textTransform = nodeSelectionLocalToWorldMatrix(document, node.id);
    for (const rect of selectionRectsForTextRange(node, rangeStart, rangeEnd, zoomBucket)) {
      const buffer = createTriangleBuffer(this.host.gl, transformRectVertices(rect, textTransform));
      twgl.setBuffersAndAttributes(this.host.gl, this.host.programInfo, buffer);
      twgl.setUniforms(this.host.programInfo, this.host.uniforms([0.125, 0.749, 1, 0.36]));
      twgl.drawBufferInfo(this.host.gl, buffer);
    }

    if (selection.focused && selection.caretVisible && selection.anchor === selection.focus) {
      const caret = caretRectForTextIndex(node, selection.focus, zoomBucket);
      const width = 1 / this.host.camera.zoom;
      const buffer = createTriangleBuffer(this.host.gl, transformRectVertices({ ...caret, x: caret.x - width / 2, width }, textTransform));
      twgl.setBuffersAndAttributes(this.host.gl, this.host.programInfo, buffer);
      twgl.setUniforms(this.host.programInfo, this.host.uniforms([0, 0, 0, 1]));
      twgl.drawBufferInfo(this.host.gl, buffer);
    }
  }

  private drawSelectionOutline(
    bounds: { x: number; y: number; width: number; height: number },
    color: [number, number, number, number] = [0.125, 0.749, 1, 1],
    rotation = 0,
    origin = rectCenter(bounds),
  ): void {
    const gl = this.host.gl;
    const vertices = rotatedBoundsVertices(bounds, rotation, origin);
    const lineBuffer = createTriangleBuffer(gl, vertices);
    twgl.setBuffersAndAttributes(gl, this.host.programInfo, lineBuffer);
    twgl.setUniforms(this.host.programInfo, this.host.uniforms(color));
    gl.lineWidth(1);
    twgl.drawBufferInfo(gl, lineBuffer, gl.LINE_LOOP);
  }

  private drawSelectionOutlineFromCorners(
    corners: Array<{ x: number; y: number }>,
    color: [number, number, number, number] = [0.125, 0.749, 1, 1],
  ): void {
    if (corners.length < 4) {
      return;
    }

    const gl = this.host.gl;
    const vertices = corners.slice(0, 4).flatMap(point => [point.x, point.y]);
    const lineBuffer = createTriangleBuffer(gl, vertices);
    twgl.setBuffersAndAttributes(gl, this.host.programInfo, lineBuffer);
    twgl.setUniforms(this.host.programInfo, this.host.uniforms(color));
    gl.lineWidth(1);
    twgl.drawBufferInfo(gl, lineBuffer, gl.LINE_LOOP);
  }

  private drawLassoOverlay(points: Array<{ x: number; y: number }>): void {
    if (points.length < 2) {
      return;
    }

    const closedPoints = points.length > 2 ? [...points, points[0]!] : points;
    this.drawSourceOverLines(pointsToLineSegments(closedPoints), [0.125, 0.749, 1, 0.9]);
  }

  private drawOrientedSelectionOutline(
    document: CurvaDocument,
    overlay: RenderOverlay,
    nodeId: string,
    bounds: { x: number; y: number; width: number; height: number },
    color: [number, number, number, number],
  ): void {
    const previews = scenePreviewsFromOverlay(overlay);
    if (nodeHasCompositeSelectionRotation(document, nodeId, previews)) {
      const corners = nodeSelectionCorners(document, nodeId, previews);
      if (corners && corners.length >= 4) {
        this.drawSelectionOutlineFromCorners(corners, color);
        return;
      }
    }

    const rotation = findNodeRotation(document, overlay, nodeId);
    const origin = findNodeRotationOrigin(document, overlay, nodeId, bounds);
    this.drawSelectionOutline(bounds, color, rotation, origin);
  }

  private drawOrientedSelectionHandles(
    document: CurvaDocument,
    overlay: RenderOverlay,
    nodeId: string,
    bounds: { x: number; y: number; width: number; height: number },
    hoveredHandle?: ResizeHandle,
  ): void {
    const previews = scenePreviewsFromOverlay(overlay);
    if (nodeHasCompositeSelectionRotation(document, nodeId, previews)) {
      const node = findSceneNode(document, nodeId);
      if (node) {
        const preview = previews.find(candidate => candidate.nodeId === nodeId);
        const localBounds = nodeSelectionLocalBounds(node, preview);
        for (const { handle, x, y } of getResizeHandlePositions(localBounds)) {
          const worldPoint = nodeSelectionPointToWorld(document, nodeId, { x, y }, previews);
          this.drawSquareGizmo(worldPoint.x, worldPoint.y, SELECTION_HANDLE_SIZE_PX, handle === hoveredHandle);
        }
        return;
      }
    }

    const rotation = findNodeRotation(document, overlay, nodeId);
    const origin = findNodeRotationOrigin(document, overlay, nodeId, bounds);
    this.drawSelectionHandles(bounds, hoveredHandle, rotation, origin);
  }

  private drawPrototypeAnchors(anchors: PrototypeAnchorOverlay[]): void {
    const gl = this.host.gl;
    gl.disable(gl.SCISSOR_TEST);
    gl.useProgram(this.host.programInfo.program);
    const radius = 9 / Math.max(0.0001, this.host.camera.zoom);
    const innerRadius = 4.5 / Math.max(0.0001, this.host.camera.zoom);

    for (const anchor of anchors) {
      const ring = createTriangleBuffer(gl, circleRingVertices(anchor.x, anchor.y, radius, innerRadius, 28));
      twgl.setBuffersAndAttributes(gl, this.host.programInfo, ring);
      twgl.setUniforms(this.host.programInfo, this.host.uniforms(anchor.active || anchor.hovered ? [0.125, 0.749, 1, 1] : [0.56, 0.78, 1, 0.82]));
      twgl.drawBufferInfo(gl, ring);

      const fill = createTriangleBuffer(gl, circleVertices(anchor.x, anchor.y, innerRadius, 24));
      twgl.setBuffersAndAttributes(gl, this.host.programInfo, fill);
      twgl.setUniforms(this.host.programInfo, this.host.uniforms([0.04, 0.07, 0.11, anchor.active ? 0.95 : 0.72]));
      twgl.drawBufferInfo(gl, fill);
    }
  }

  private drawPrototypeLinks(
    links: PrototypeLinkOverlay[],
    preview?: SelectionOverlay["prototypeLinkPreview"],
  ): void {
    const gl = this.host.gl;
    gl.disable(gl.SCISSOR_TEST);
    gl.useProgram(this.host.programInfo.program);
    const color: [number, number, number, number] = [0.125, 0.749, 1, 0.92];
    const selectedColor: [number, number, number, number] = [0.56, 0.78, 1, 1];
    const previewColor: [number, number, number, number] = [0.56, 0.78, 1, 0.72];

    for (const link of links) {
      this.drawPrototypeArrow(link.from, link.to, link.selected ? selectedColor : color, link.selected ? 3 : 2);
    }

    if (preview) {
      this.drawPrototypeArrow(preview.from, preview.to, previewColor);
    }
  }

  private drawPrototypeArrow(
    from: { x: number; y: number },
    to: { x: number; y: number },
    color: [number, number, number, number],
    widthPx = 2,
  ): void {
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const length = Math.hypot(dx, dy);
    if (length < 0.001) {
      return;
    }

    const ux = dx / length;
    const uy = dy / length;
    const lineWidth = widthPx / Math.max(0.0001, this.host.camera.zoom);
    const headLength = 12 / Math.max(0.0001, this.host.camera.zoom);
    const headWidth = 8 / Math.max(0.0001, this.host.camera.zoom);
    const lineEnd = {
      x: to.x - ux * Math.min(headLength, length * 0.45),
      y: to.y - uy * Math.min(headLength, length * 0.45),
    };
    const vertices = [
      ...thickLineVertices(from, lineEnd, lineWidth),
      to.x,
      to.y,
      lineEnd.x + -uy * headWidth / 2,
      lineEnd.y + ux * headWidth / 2,
      lineEnd.x - -uy * headWidth / 2,
      lineEnd.y - ux * headWidth / 2,
    ];
    const buffer = createTriangleBuffer(this.host.gl, vertices);
    twgl.setBuffersAndAttributes(this.host.gl, this.host.programInfo, buffer);
    twgl.setUniforms(this.host.programInfo, this.host.uniforms(color));
    twgl.drawBufferInfo(this.host.gl, buffer);
  }

  private drawObjectSnapGuides(guides: ObjectSnapGuideOverlay[]): void {
    const vertices = guides.flatMap(guide =>
      guide.x1 !== undefined && guide.y1 !== undefined && guide.x2 !== undefined && guide.y2 !== undefined
        ? [guide.x1, guide.y1, guide.x2, guide.y2]
        : guide.orientation === "vertical"
        ? [guide.position, guide.from, guide.position, guide.to]
        : [guide.from, guide.position, guide.to, guide.position],
    );
    if (vertices.length === 0) {
      return;
    }

    const gl = this.host.gl;
    gl.disable(gl.SCISSOR_TEST);
    gl.useProgram(this.host.programInfo.program);
    gl.lineWidth(1);
    const buffer = createTriangleBuffer(gl, vertices);
    twgl.setBuffersAndAttributes(gl, this.host.programInfo, buffer);
    twgl.setUniforms(this.host.programInfo, this.host.uniforms(SNAP_GUIDE_COLOR));
    twgl.drawBufferInfo(gl, buffer, gl.LINES);
  }

  private drawDocumentGuides(
    guides: DocumentGuideOverlay[],
    preview?: GuidePreviewOverlay,
  ): void {
    const gl = this.host.gl;
    gl.disable(gl.SCISSOR_TEST);
    gl.useProgram(this.host.programInfo.program);
    const lineWidth = 1 / Math.max(0.0001, this.host.camera.zoom);
    const span = Math.max(this.host.canvas.width, this.host.canvas.height) / Math.max(0.0001, this.host.camera.zoom) * 4;
    const cameraCenterY = this.host.camera.y + this.host.canvas.height / (2 * Math.max(0.0001, this.host.camera.zoom));
    const cameraCenterX = this.host.camera.x + this.host.canvas.width / (2 * Math.max(0.0001, this.host.camera.zoom));

    const drawGuideLine = (
      orientation: GuideOrientation,
      documentPosition: number,
      clipBounds?: Rect,
      selected = false,
    ): void => {
      const color: [number, number, number, number] = selected ? GIZMO_BLUE : [1, 0, 0, 1];
      let bounds: { x: number; y: number; width: number; height: number };
      if (orientation === "vertical") {
        const x = documentPosition - lineWidth / 2;
        if (clipBounds) {
          bounds = {
            x,
            y: Math.min(clipBounds.y, clipBounds.y + clipBounds.height),
            width: lineWidth,
            height: Math.abs(clipBounds.height),
          };
        } else {
          bounds = { x, y: cameraCenterY - span, width: lineWidth, height: span * 2 };
        }
      } else if (clipBounds) {
        bounds = {
          x: Math.min(clipBounds.x, clipBounds.x + clipBounds.width),
          y: documentPosition - lineWidth / 2,
          width: Math.abs(clipBounds.width),
          height: lineWidth,
        };
      } else {
        bounds = { x: cameraCenterX - span, y: documentPosition - lineWidth / 2, width: span * 2, height: lineWidth };
      }

      const buffer = createTriangleBuffer(gl, rectVertices(bounds));
      twgl.setBuffersAndAttributes(gl, this.host.programInfo, buffer);
      twgl.setUniforms(this.host.programInfo, this.host.uniforms(color));
      twgl.drawBufferInfo(gl, buffer);
    };

    for (const guide of guides) {
      drawGuideLine(guide.orientation, guide.documentPosition, guide.clipBounds, guide.selected);
    }

    if (preview) {
      drawGuideLine(preview.orientation, preview.documentPosition, undefined, preview.selected ?? false);
    }
  }

  private drawMeasureOverlay(
    overlay: MeasureOverlay,
    visibleViewportInsets?: ViewportScreenInsets,
  ): void {
    const solidVertices: number[] = [];
    const dashedVertices: number[] = [];
    const dashLength = 4 / this.host.camera.zoom;
    const gapLength = 4 / this.host.camera.zoom;

    for (const line of overlay.lines) {
      const target = line.dashed ? dashedVertices : solidVertices;
      target.push(...measureLineVertices(line.x1, line.y1, line.x2, line.y2, line.dashed ? dashLength : undefined, line.dashed ? gapLength : undefined));
    }

    const gl = this.host.gl;
    gl.disable(gl.SCISSOR_TEST);
    gl.useProgram(this.host.programInfo.program);
    gl.lineWidth(1);

    if (solidVertices.length > 0) {
      const buffer = createTriangleBuffer(gl, solidVertices);
      twgl.setBuffersAndAttributes(gl, this.host.programInfo, buffer);
      twgl.setUniforms(this.host.programInfo, this.host.uniforms(SNAP_GUIDE_COLOR));
      twgl.drawBufferInfo(gl, buffer, gl.LINES);
    }

    if (dashedVertices.length > 0) {
      const buffer = createTriangleBuffer(gl, dashedVertices);
      twgl.setBuffersAndAttributes(gl, this.host.programInfo, buffer);
      twgl.setUniforms(this.host.programInfo, this.host.uniforms([SNAP_GUIDE_COLOR[0], SNAP_GUIDE_COLOR[1], SNAP_GUIDE_COLOR[2], 0.75]));
      twgl.drawBufferInfo(gl, buffer, gl.LINES);
    }

    for (const label of overlay.labels) {
      this.drawMeasureLabel(label.x, label.y, label.text, visibleViewportInsets);
    }
  }

  private drawMeasureLabel(
    x: number,
    y: number,
    text: string,
    visibleViewportInsets?: ViewportScreenInsets,
  ): void {
    const dpr = Math.max(1, this.host.camera.devicePixelRatio);
    const fontSize = 10;
    const paddingX = 4;
    const paddingY = 2;
    const canvas = document.createElement("canvas");
    const measureContext = canvas.getContext("2d");
    if (!measureContext) {
      return;
    }

    measureContext.font = `${fontSize}px ui-sans-serif, system-ui, -apple-system, sans-serif`;
    const textWidth = measureContext.measureText(text).width;
    const cssWidth = Math.max(18, textWidth + paddingX * 2);
    const cssHeight = fontSize + paddingY * 2;
    canvas.width = Math.ceil(cssWidth * dpr);
    canvas.height = Math.ceil(cssHeight * dpr);
    const context = canvas.getContext("2d");
    if (!context) {
      return;
    }

    context.scale(dpr, dpr);
    context.font = `${fontSize}px ui-sans-serif, system-ui, -apple-system, sans-serif`;
    context.fillStyle = "rgba(255, 31, 41, 0.95)";
    context.beginPath();
    context.roundRect(0, 0, cssWidth, cssHeight, 3);
    context.fill();
    context.fillStyle = "#ffffff";
    context.textAlign = "center";
    context.textBaseline = "middle";
    context.fillText(text, cssWidth / 2, cssHeight / 2 + 0.5);

    let labelCenterX = x;
    let labelCenterY = y;
    if (visibleViewportInsets) {
      const screenCenter = documentPointToScreen({ x, y }, this.host.canvas, this.host.camera);
      const clampedScreen = clampScreenPointToInsets(
        screenCenter,
        { width: cssWidth, height: cssHeight },
        this.host.canvas,
        visibleViewportInsets,
      );
      const clampedDocument = screenPointToDocument(clampedScreen, this.host.canvas, this.host.camera);
      labelCenterX = clampedDocument.x;
      labelCenterY = clampedDocument.y;
    }

    const bounds = {
      x: labelCenterX - cssWidth / 2 / this.host.camera.zoom,
      y: labelCenterY - cssHeight / 2 / this.host.camera.zoom,
      width: cssWidth / this.host.camera.zoom,
      height: cssHeight / this.host.camera.zoom,
    };
    const texture = this.host.uploadCanvasTexture(canvas);
    if (texture) {
      this.host.drawCachedWebGLTexture(texture, bounds);
      this.host.gl.deleteTexture(texture);
    }
  }

  private drawDimensionLabel(bounds: RenderPreview["bounds"]): void {
    const label = dimensionLabelText(bounds);
    if (!label) {
      return;
    }

    const dpr = Math.max(1, this.host.camera.devicePixelRatio);
    const cacheKey = `${label}:${dpr}`;
    let cached = this.host.dimensionLabelTextureCache.get(cacheKey);
    if (!cached) {
      const canvas = this.rasterizeDimensionLabel(label);
      const texture = this.host.uploadCanvasTexture(canvas);
      if (!texture) {
        return;
      }
      cached = {
        texture,
        width: canvas.width / dpr,
        height: canvas.height / dpr,
      };
      this.host.dimensionLabelTextureCache.set(cacheKey, cached);
      if (this.host.dimensionLabelTextureCache.size > 80) {
        const firstKey = this.host.dimensionLabelTextureCache.keys().next().value;
        if (firstKey !== undefined) {
          const first = this.host.dimensionLabelTextureCache.get(firstKey);
          if (first) {
            this.host.gl.deleteTexture(first.texture);
            this.host.dimensionLabelTextureCache.delete(firstKey);
          }
        }
      }
    }

    const left = Math.min(bounds.x, bounds.x + bounds.width);
    const right = Math.max(bounds.x, bounds.x + bounds.width);
    const bottom = Math.max(bounds.y, bounds.y + bounds.height);
    const gapPx = 8;
    const labelBounds = {
      x: (left + right) / 2 - cached.width / 2 / this.host.camera.zoom,
      y: bottom + gapPx / this.host.camera.zoom,
      width: cached.width / this.host.camera.zoom,
      height: cached.height / this.host.camera.zoom,
    };
    this.host.drawCachedWebGLTexture(cached.texture, labelBounds);
  }

  private rasterizeDimensionLabel(text: string): HTMLCanvasElement {
    const dpr = Math.max(1, this.host.camera.devicePixelRatio);
    const fontSize = 11;
    const paddingX = 5;
    const paddingY = 2;
    const canvas = document.createElement("canvas");
    const measureContext = canvas.getContext("2d");
    if (!measureContext) {
      return canvas;
    }

    measureContext.font = `700 ${fontSize}px ui-monospace, SFMono-Regular, Menlo, monospace`;
    const textWidth = measureContext.measureText(text).width;
    const cssWidth = Math.ceil(Math.max(28, textWidth + paddingX * 2));
    const cssHeight = fontSize + paddingY * 2;
    canvas.width = Math.max(1, Math.ceil(cssWidth * dpr));
    canvas.height = Math.max(1, Math.ceil(cssHeight * dpr));
    const context = canvas.getContext("2d");
    if (!context) {
      return canvas;
    }

    context.scale(dpr, dpr);
    context.font = `700 ${fontSize}px ui-monospace, SFMono-Regular, Menlo, monospace`;
    context.textAlign = "center";
    context.textBaseline = "middle";
    roundedRectPath(context, { x: 0, y: 0, width: cssWidth, height: cssHeight }, [3, 3, 3, 3]);
    context.fillStyle = "rgba(13, 153, 255, 0.96)";
    context.fill();
    context.fillStyle = "#ffffff";
    context.fillText(text, cssWidth / 2, cssHeight / 2 + 0.5);
    return canvas;
  }

  private drawPixelGrid(): void {
    const viewport = this.host.viewportDocumentBounds();
    const scale = this.host.camera.zoom * this.host.camera.devicePixelRatio;
    const startX = Math.floor(viewport.x);
    const endX = Math.ceil(viewport.x + viewport.width);
    const startY = Math.floor(viewport.y);
    const endY = Math.ceil(viewport.y + viewport.height);
    const vertices: number[] = [];

    for (let x = startX; x <= endX; x += 1) {
      const alignedX = this.host.camera.x + (Math.round((x - this.host.camera.x) * scale) + 0.5) / scale;
      vertices.push(alignedX, viewport.y, alignedX, viewport.y + viewport.height);
    }

    for (let y = startY; y <= endY; y += 1) {
      const alignedY = this.host.camera.y + (Math.round((y - this.host.camera.y) * scale) + 0.5) / scale;
      vertices.push(viewport.x, alignedY, viewport.x + viewport.width, alignedY);
    }

    const gl = this.host.gl;
    gl.disable(gl.SCISSOR_TEST);
    gl.useProgram(this.host.programInfo.program);
    gl.lineWidth(1);
    this.drawGridLines(vertices, [0, 0, 0, 0.08]);
    this.drawGridLines(vertices, [1, 1, 1, 0.05]);
  }

  private drawFrameLabels(
    labels: FrameLabelOverlay[],
    hoveredFrameLabelId: string | undefined,
    selectedNodeIds: Set<string>,
  ): void {
    const viewport = this.host.viewportDocumentBounds();
    this.host.gl.disable(this.host.gl.SCISSOR_TEST);
    for (const label of labels) {
      const hasScript = Boolean(label.hasScript);
      const geometry = frameLabelGeometryForCorners(label.corners, this.host.camera.zoom, {
        extraWidthPx: hasScript ? FRAME_LABEL_SCRIPT_ACTIONS_WIDTH_PX : 0,
      });
      if (!geometry) {
        continue;
      }

      const visualBounds = boundsFromLabelPoints(geometry.corners);
      if (!rectsIntersect(visualBounds, viewport)) {
        continue;
      }

      const cssWidth = geometry.width * this.host.camera.zoom;
      const selected = selectedNodeIds.has(label.id);
      const hovered = hoveredFrameLabelId === label.id;
      const kind = label.kind ?? "frame";
      const cacheKey = this.frameLabelCacheKey(label.id, label.name, cssWidth, selected, hovered, kind, hasScript);
      let texture = this.host.frameLabelTextureCache.get(cacheKey);
      if (!texture) {
        const canvas = this.rasterizeFrameLabel(label.name, cssWidth, selected, hovered, kind, hasScript);
        texture = this.host.uploadCanvasTexture(canvas);
        if (texture) {
          this.host.frameLabelTextureCache.set(cacheKey, texture);
        }
        this.host.recordLabelRaster();
      } else {
        this.host.recordLabelCacheHit();
      }

      if (texture) {
        this.host.drawCachedWebGLTexture(texture, geometry);
      }
    }
    this.host.gl.useProgram(this.host.programInfo.program);
  }

  private frameLabelCacheKey(
    labelId: string,
    name: string,
    cssWidth: number,
    selected: boolean,
    hovered: boolean,
    kind: "frame" | "component",
    hasScript: boolean,
  ): string {
    const widthBucket = Math.round(cssWidth / 4) * 4;
    const dpr = Math.max(1, this.host.camera.devicePixelRatio);
    return `${labelId}:${name}:${kind}:${selected}:${hovered}:${hasScript}:${widthBucket}:${dpr}`;
  }
  private drawGridLines(vertices: number[], color: [number, number, number, number]): void {
    if (vertices.length === 0) {
      return;
    }

    const buffer = createTriangleBuffer(this.host.gl, vertices);
    twgl.setBuffersAndAttributes(this.host.gl, this.host.programInfo, buffer);
    twgl.setUniforms(this.host.programInfo, this.host.uniforms(color));
    twgl.drawBufferInfo(this.host.gl, buffer, this.host.gl.LINES);
  }

  private drawDraftShape(kind: PrimitiveKind, bounds: RenderPreview["bounds"]): void {
    if (kind !== "line" && (bounds.width <= 0 || bounds.height <= 0)) {
      return;
    }

    const gl = this.host.gl;
    if (kind === "oval") {
      const bufferInfo = createTriangleBuffer(gl, rectVertices(bounds));
      gl.useProgram(this.host.ellipseProgramInfo.program);
      twgl.setBuffersAndAttributes(gl, this.host.ellipseProgramInfo, bufferInfo);
      twgl.setUniforms(this.host.ellipseProgramInfo, {
        ...this.host.uniforms([0.219, 0.741, 0.973, 0.22]),
        u_bounds: [bounds.x, bounds.y, bounds.width, bounds.height],
      });
      twgl.drawBufferInfo(gl, bufferInfo);
      gl.useProgram(this.host.programInfo.program);
      this.drawSelectionOutline(bounds);
      return;
    }

    const draftNode: PrimitiveNode = {
      id: "__draft_shape__",
      name: "Draft Shape",
      type: "primitive",
      visible: true,
      locked: false,
      transform: { x: 0, y: 0, rotation: 0, scaleX: 1, scaleY: 1 },
      bounds,
      style: createDefaultStyle("#38bdf8"),
      primitive: draftPrimitive(kind),
    };
    const vertices = primitiveToTriangles(draftNode);
    const bufferInfo = createTriangleBuffer(gl, vertices);
    twgl.setBuffersAndAttributes(gl, this.host.programInfo, bufferInfo);
    twgl.setUniforms(this.host.programInfo, this.host.uniforms([0.219, 0.741, 0.973, 0.22]));
    twgl.drawBufferInfo(gl, bufferInfo);
    if (kind !== "line") {
      this.drawSelectionOutline(bounds);
    }
  }

  private drawSelectionHandles(bounds: { x: number; y: number; width: number; height: number }, hoveredHandle?: ResizeHandle, rotation = 0, origin = rectCenter(bounds)): void {
    for (const { handle, x, y } of getResizeHandlePositions(bounds)) {
      const point = rotatePoint({ x, y }, origin, rotation);
      this.drawSquareGizmo(point.x, point.y, SELECTION_HANDLE_SIZE_PX, handle === hoveredHandle);
    }
  }

  private drawVectorEditOverlay(document: CurvaDocument, overlay: RenderOverlay, vectorPreview?: VectorNetworkPreview): void {
    const node = getAllSceneNodes(document).find(candidate => candidate.id === overlay.vectorEditNodeId);
    if (node?.type !== "vectorNetwork") {
      return;
    }

    const localDisplayNode = vectorPreview?.id === node.id
      ? { ...node, bounds: vectorPreview.bounds, network: vectorPreview.network }
      : node;
    const previews = scenePreviewsFromOverlay(overlay);
    const displayNode = vectorEditNodeToWorld(document, localDisplayNode, previews);
    const displayOverlay = vectorEditOverlayToWorld(document, overlay, localDisplayNode, previews);

    this.drawVectorEditEdges(displayNode, displayOverlay);
    this.drawKnifePreview(displayOverlay);
    this.drawShapeBuilderRegions(displayOverlay);
    if (displayOverlay.sculptBrushPreview?.nodeId === node.id) {
      this.drawSculptBrush(displayOverlay.sculptBrushPreview);
    }
    this.drawVectorEditHandles(displayNode, displayOverlay);
    this.drawVectorEditVertices(displayNode, displayOverlay);
    if (overlay.vectorSmartTransform?.nodeId === node.id) {
      this.drawVectorSmartTransformGizmo(document, node.id, overlay.vectorSmartTransform, overlay, overlay.hoveredResizeHandle);
    }

    if (displayOverlay.pathPreviewPoint?.nodeId === node.id) {
      this.drawCircleGizmo(displayOverlay.pathPreviewPoint.x, displayOverlay.pathPreviewPoint.y, PATH_PREVIEW_RADIUS_PX, false, false);
    }

    if (displayOverlay.pathPreviewSegment?.nodeId === node.id) {
      this.drawPathPreviewSegment(displayOverlay.pathPreviewSegment.from, displayOverlay.pathPreviewSegment.to);
    }
  }

  private drawVectorSmartTransformGizmo(
    document: CurvaDocument,
    nodeId: string,
    transform: VectorSmartTransformOverlay,
    overlay: RenderOverlay,
    hoveredHandle?: ResizeHandle,
  ): void {
    const previews = scenePreviewsFromOverlay(overlay);
    const matrix = nodeSelectionLocalToWorldMatrix(document, nodeId, previews);
    const origin = rectCenter(transform.bounds);
    const localHandles = getResizeHandlePositions(transform.bounds).map(({ handle, x, y }) => ({
      handle,
      point: rotatePoint({ x, y }, origin, transform.rotation),
    }));

    if (!affineEquals(matrix, IDENTITY_AFFINE)) {
      const localCorners = rectCorners(transform.bounds).map(corner => rotatePoint(corner, origin, transform.rotation));
      this.drawSelectionOutlineFromCorners(
        localCorners.map(corner => applyAffinePoint(matrix, corner)),
        [0.125, 0.749, 1, 1],
      );
      for (const { handle, point } of localHandles) {
        const worldPoint = applyAffinePoint(matrix, point);
        this.drawSquareGizmo(worldPoint.x, worldPoint.y, SELECTION_HANDLE_SIZE_PX, handle === hoveredHandle);
      }
      return;
    }

    this.drawSelectionOutline(transform.bounds, [0.125, 0.749, 1, 1], transform.rotation, origin);
    for (const { handle, point } of localHandles) {
      this.drawSquareGizmo(point.x, point.y, SELECTION_HANDLE_SIZE_PX, handle === hoveredHandle);
    }
  }

  private drawKnifePreview(overlay: RenderOverlay): void {
    if (!overlay.knifePreview || overlay.knifePreview.points.length < 2) {
      return;
    }
    const vertices = pointsToLineSegments(overlay.knifePreview.points);
    this.drawSourceOverLines(vertices, [1, 0.18, 0.18, 0.95]);
  }

  private drawSculptBrush(brush: NonNullable<RenderOverlay["sculptBrushPreview"]>): void {
    const segments: number[] = [];
    const steps = 64;
    for (let index = 0; index < steps; index += 1) {
      const start = (index / steps) * Math.PI * 2;
      const end = ((index + 1) / steps) * Math.PI * 2;
      segments.push(
        brush.x + Math.cos(start) * brush.radius,
        brush.y + Math.sin(start) * brush.radius,
        brush.x + Math.cos(end) * brush.radius,
        brush.y + Math.sin(end) * brush.radius,
      );
    }
    this.drawSourceOverLines(segments, [0.3, 0.85, 1, 0.95]);
  }

  private drawPathPreviewSegment(from: { x: number; y: number }, to: { x: number; y: number }): void {
    this.drawInvertedLines([from.x, from.y, to.x, to.y]);

    this.drawCircleGizmo(to.x, to.y, PATH_PREVIEW_RADIUS_PX, false, false);
  }

  private drawShapeBuilderRegions(overlay: RenderOverlay): void {
    if (!overlay.shapeBuilderRegions?.length) {
      return;
    }

    for (const region of overlay.shapeBuilderRegions) {
      const vertices = region.contour.flatMap(point => [point.x, point.y]);
      if (vertices.length < 6) {
        continue;
      }

      const indices = earcut(vertices);
      const polygonTriangles = indices.flatMap(index => [vertices[index * 2]!, vertices[index * 2 + 1]!]);
      if (polygonTriangles.length < 6) {
        continue;
      }

      const buffer = createTriangleBuffer(this.host.gl, polygonTriangles);
      twgl.setBuffersAndAttributes(this.host.gl, this.host.programInfo, buffer);
      const color: [number, number, number, number] = region.state === "remove"
        ? [1, 0.22, 0.22, 0.34]
        : region.state === "add"
          ? [0.125, 0.749, 1, 0.34]
          : [0.56, 0.78, 1, 0.24];
      twgl.setUniforms(this.host.programInfo, this.host.uniforms(color));
      twgl.drawBufferInfo(this.host.gl, buffer, this.host.gl.TRIANGLES);

      const outline = [...vertices, vertices[0]!, vertices[1]!];
      const outlineBuffer = createTriangleBuffer(this.host.gl, outline);
      twgl.setBuffersAndAttributes(this.host.gl, this.host.programInfo, outlineBuffer);
      twgl.setUniforms(this.host.programInfo, this.host.uniforms([color[0]!, color[1]!, color[2]!, 0.82]));
      twgl.drawBufferInfo(this.host.gl, outlineBuffer, this.host.gl.LINE_STRIP);
    }
  }

  private drawHandleControl(
    vertex: { x: number; y: number },
    handle: { x: number; y: number },
    options: { selected: boolean; hovered: boolean },
  ): void {
    this.drawInvertedLines([vertex.x, vertex.y, handle.x, handle.y]);

    this.drawCircleGizmo(
      handle.x,
      handle.y,
      options.selected ? SELECTED_BEZIER_HANDLE_RADIUS_PX : BEZIER_HANDLE_RADIUS_PX,
      options.selected,
      options.hovered,
    );
  }

  private drawVectorEditVertices(node: Extract<SceneNode, { type: "vectorNetwork" }>, overlay: RenderOverlay): void {
    const preview = overlay.vertexPreview?.nodeId === node.id ? overlay.vertexPreview : undefined;
    const previewById = new Map((preview?.vertices ?? (preview ? [preview] : [])).map(vertex => [vertex.vertexId, vertex]));
    const interactiveVertexIds = new Set<string>();
    if (overlay.hoveredVertexId) {
      interactiveVertexIds.add(overlay.hoveredVertexId);
    }
    if (overlay.selectedVertexId) {
      interactiveVertexIds.add(overlay.selectedVertexId);
    }
    for (const vertexId of overlay.selectedVertexIds ?? []) {
      interactiveVertexIds.add(vertexId);
    }
    for (const vertexId of overlay.previewSelectedVertexIds ?? []) {
      interactiveVertexIds.add(vertexId);
    }
    for (const vertexId of overlay.freshCutVertexIds ?? []) {
      interactiveVertexIds.add(vertexId);
    }
    for (const vertexId of overlay.vectorTopologyPreview?.nodeId === node.id
      ? overlay.vectorTopologyPreview.newVertexIds
      : []) {
      interactiveVertexIds.add(vertexId);
    }
    for (const vertexId of previewById.keys()) {
      interactiveVertexIds.add(vertexId);
    }

    const radius = VERTEX_RADIUS_PX / this.host.camera.zoom;
    const stroke = CIRCLE_STROKE_PX / this.host.camera.zoom;
    const fillRadius = Math.max(0, radius - stroke);
    const batchedFills: number[] = [];
    const batchedRings: number[] = [];

    for (const vertex of node.network.vertices) {
      if (interactiveVertexIds.has(vertex.id)) {
        continue;
      }

      batchedFills.push(...circleVertices(vertex.x, vertex.y, fillRadius));
      batchedRings.push(...circleRingVertices(vertex.x, vertex.y, radius, fillRadius));
    }

    this.drawColoredTriangles(batchedFills, GIZMO_WHITE);
    this.drawColoredTriangles(batchedRings, GIZMO_BLUE);

    for (const vertex of node.network.vertices) {
      if (!interactiveVertexIds.has(vertex.id)) {
        continue;
      }

      const previewVertex = previewById.get(vertex.id);
      const isPreview = Boolean(previewVertex);
      const selected = overlay.selectedVertexId === vertex.id || (overlay.selectedVertexIds ?? []).includes(vertex.id) || isPreview;
      const hovered = overlay.hoveredVertexId === vertex.id;
      const freshCut = (overlay.freshCutVertexIds ?? []).includes(vertex.id);
      if (freshCut && !isPreview) {
        this.drawCircleGizmoWithColor(vertex.x, vertex.y, selected ? SELECTED_VERTEX_RADIUS_PX : VERTEX_RADIUS_PX, [1, 0.18, 0.18, 1], hovered);
        continue;
      }
      this.drawCircleGizmo(
        previewVertex ? previewVertex.x : vertex.x,
        previewVertex ? previewVertex.y : vertex.y,
        selected ? SELECTED_VERTEX_RADIUS_PX : VERTEX_RADIUS_PX,
        selected,
        hovered,
      );
    }
  }

  private drawVectorEditHandles(node: Extract<SceneNode, { type: "vectorNetwork" }>, overlay: RenderOverlay): void {
    const vertexById = new Map(node.network.vertices.map(vertex => [vertex.id, vertex]));
    const selectedVertexIds = new Set([overlay.selectedVertexId, ...(overlay.selectedVertexIds ?? [])].filter((id): id is string => Boolean(id)));
    const selectedHandleVertexId = overlay.selectedHandle
      ? node.network.edges.find(edge => edge.id === overlay.selectedHandle?.edgeId)?.[overlay.selectedHandle.endpoint].vertexId
      : undefined;
    const handleLines: number[] = [];

    for (const edge of node.network.edges) {
      const fromSelected = overlay.selectedHandle?.edgeId === edge.id && overlay.selectedHandle.endpoint === "from";
      const toSelected = overlay.selectedHandle?.edgeId === edge.id && overlay.selectedHandle.endpoint === "to";
      const fromHovered = overlay.hoveredVectorHandle?.edgeId === edge.id && overlay.hoveredVectorHandle.endpoint === "from";
      const toHovered = overlay.hoveredVectorHandle?.edgeId === edge.id && overlay.hoveredVectorHandle.endpoint === "to";
      const fromHandle = previewHandleFor(overlay, edge.id, "from") ?? edge.from.handle;
      const toHandle = previewHandleFor(overlay, edge.id, "to") ?? edge.to.handle;
      const drawFrom = fromHandle && (
        fromSelected ||
        selectedVertexIds.has(edge.from.vertexId) ||
        edge.from.vertexId === selectedHandleVertexId
      );
      const drawTo = toHandle && (
        toSelected ||
        selectedVertexIds.has(edge.to.vertexId) ||
        edge.to.vertexId === selectedHandleVertexId
      );

      if (drawFrom && fromHandle) {
        const vertex = vertexById.get(edge.from.vertexId);
        if (vertex) {
          handleLines.push(vertex.x, vertex.y, fromHandle.x, fromHandle.y);
          this.drawCircleGizmo(
            fromHandle.x,
            fromHandle.y,
            fromSelected ? SELECTED_BEZIER_HANDLE_RADIUS_PX : BEZIER_HANDLE_RADIUS_PX,
            fromSelected,
            fromHovered,
          );
        }
      }

      if (drawTo && toHandle) {
        const vertex = vertexById.get(edge.to.vertexId);
        if (vertex) {
          handleLines.push(vertex.x, vertex.y, toHandle.x, toHandle.y);
          this.drawCircleGizmo(
            toHandle.x,
            toHandle.y,
            toSelected ? SELECTED_BEZIER_HANDLE_RADIUS_PX : BEZIER_HANDLE_RADIUS_PX,
            toSelected,
            toHovered,
          );
        }
      }
    }

    if (handleLines.length > 0) {
      this.drawInvertedLines(handleLines);
    }
  }

  private drawSquareGizmo(x: number, y: number, sizePx: number, hovered: boolean): void {
    const size = (sizePx + (hovered ? GIZMO_HOVER_DELTA_PX : 0)) / this.host.camera.zoom;
    const stroke = GIZMO_STROKE_PX / this.host.camera.zoom;
    const outer = centeredRect(x, y, size);
    const inner = centeredRect(x, y, Math.max(0, size - stroke * 2));
    this.drawColoredTriangles(rectVertices(outer), GIZMO_BLUE);
    this.drawColoredTriangles(rectVertices(inner), GIZMO_WHITE);
  }

  private drawCircleGizmo(x: number, y: number, radiusPx: number, selected: boolean, hovered: boolean): void {
    const radius = (radiusPx + (hovered ? GIZMO_HOVER_DELTA_PX / 2 : 0)) / this.host.camera.zoom;
    const stroke = CIRCLE_STROKE_PX / this.host.camera.zoom;
    const fillRadius = Math.max(0, radius - stroke);

    if (selected) {
      this.drawColoredTriangles(circleVertices(x, y, fillRadius), GIZMO_BLUE);
      if (stroke > 0) {
        this.drawColoredTriangles(circleRingVertices(x, y, radius, fillRadius), GIZMO_WHITE);
      }
      return;
    }

    this.drawColoredTriangles(circleVertices(x, y, fillRadius), GIZMO_WHITE);
    if (stroke > 0) {
      this.drawColoredTriangles(circleRingVertices(x, y, radius, fillRadius), GIZMO_BLUE);
    }
  }

  private drawCircleGizmoWithColor(x: number, y: number, radiusPx: number, color: [number, number, number, number], hovered: boolean): void {
    const radius = (radiusPx + (hovered ? GIZMO_HOVER_DELTA_PX / 2 : 0)) / this.host.camera.zoom;
    const stroke = CIRCLE_STROKE_PX / this.host.camera.zoom;
    const fillRadius = Math.max(0, radius - stroke);
    this.drawColoredTriangles(circleVertices(x, y, fillRadius), GIZMO_WHITE);
    if (stroke > 0) {
      this.drawColoredTriangles(circleRingVertices(x, y, radius, fillRadius), color);
    }
  }

  private drawColoredTriangles(vertices: number[], color: [number, number, number, number]): void {
    if (vertices.length === 0) {
      return;
    }

    const buffer = this.host.displayTriangleBuffer(vertices);
    twgl.setBuffersAndAttributes(this.host.gl, this.host.programInfo, buffer);
    twgl.setUniforms(this.host.programInfo, this.host.uniforms(color));
    twgl.drawBufferInfo(this.host.gl, buffer);
  }

  private drawInvertedLines(vertices: number[], lineWidth = 1): void {
    if (vertices.length === 0) {
      return;
    }

    const buffer = createTriangleBuffer(this.host.gl, vertices);
    twgl.setBuffersAndAttributes(this.host.gl, this.host.programInfo, buffer);
    this.host.gl.blendEquation(this.host.gl.FUNC_ADD);
    this.host.gl.blendFuncSeparate(this.host.gl.ONE_MINUS_DST_COLOR, this.host.gl.ZERO, this.host.gl.ONE, this.host.gl.ZERO);
    twgl.setUniforms(this.host.programInfo, this.host.uniforms([1, 1, 1, 1]));
    this.host.gl.lineWidth(lineWidth);
    twgl.drawBufferInfo(this.host.gl, buffer, this.host.gl.LINES);
    this.host.gl.blendFunc(this.host.gl.SRC_ALPHA, this.host.gl.ONE_MINUS_SRC_ALPHA);
  }

  private drawSourceOverLines(vertices: number[], color: [number, number, number, number], lineWidth = 1): void {
    if (vertices.length === 0) {
      return;
    }

    const buffer = createTriangleBuffer(this.host.gl, vertices);
    twgl.setBuffersAndAttributes(this.host.gl, this.host.programInfo, buffer);
    this.host.gl.blendEquation(this.host.gl.FUNC_ADD);
    this.host.gl.blendFunc(this.host.gl.SRC_ALPHA, this.host.gl.ONE_MINUS_SRC_ALPHA);
    twgl.setUniforms(this.host.programInfo, this.host.uniforms(color));
    this.host.gl.lineWidth(lineWidth);
    twgl.drawBufferInfo(this.host.gl, buffer, this.host.gl.LINES);
  }

  private drawVectorGizmo(
    x: number,
    y: number,
    options: { selected: boolean; radiusPx: number; color: [number, number, number, number] },
  ): void {
    const radius = options.radiusPx / this.host.camera.zoom;
    const strokeWidth = 2 / this.host.camera.zoom;
    const vertices = options.selected
      ? circleVertices(x, y, radius)
      : circleRingVertices(x, y, radius, Math.max(0, radius - strokeWidth));
    const buffer = createTriangleBuffer(this.host.gl, vertices);
    twgl.setBuffersAndAttributes(this.host.gl, this.host.programInfo, buffer);
    twgl.setUniforms(this.host.programInfo, this.host.uniforms(options.color));
    twgl.drawBufferInfo(this.host.gl, buffer);
  }

  private drawVectorEditEdges(node: Extract<SceneNode, { type: "vectorNetwork" }>, overlay: RenderOverlay): void {
    const selectedEdgeIds = new Set([
      ...(overlay.selectedEdgeIds ?? []),
      ...(overlay.previewSelectedEdgeIds ?? []),
    ]);
    const hoveredEdgeId = overlay.hoveredEdgeId;
    const highlightedEdgeIds = new Set([
      ...selectedEdgeIds,
      ...(hoveredEdgeId ? [hoveredEdgeId] : []),
      ...(overlay.knifePreview?.edgeIds ?? []),
    ]);
    if (highlightedEdgeIds.size === 0) {
      const vertices = this.cachedVectorEditEdges(node.network);
      if (vertices.length > 0) {
        this.drawInvertedLines(vertices);
      }
      return;
    }

    const vertexById = new Map(node.network.vertices.map(vertex => [vertex.id, vertex]));
    const unselectedVertices = node.network.edges
      .filter(edge => !highlightedEdgeIds.has(edge.id))
      .flatMap(edge => pointsToLineSegments(sampleVectorEdgeFromMap(vertexById, edge, adaptiveVectorEditStepsForEdge(vertexById, edge, this.host.camera.zoom))));
    if (unselectedVertices.length > 0) {
      this.drawInvertedLines(unselectedVertices);
    }

    if (hoveredEdgeId && !selectedEdgeIds.has(hoveredEdgeId)) {
      const hoveredVertices = node.network.edges
        .filter(edge => edge.id === hoveredEdgeId)
        .flatMap(edge => pointsToLineSegments(sampleVectorEdgeFromMap(vertexById, edge, adaptiveVectorEditStepsForEdge(vertexById, edge, this.host.camera.zoom))));
      this.drawSourceOverLines(hoveredVertices, VECTOR_EDIT_HOVERED_EDGE_COLOR);
    }

    const selectedVertices = node.network.edges
      .filter(edge => selectedEdgeIds.has(edge.id))
      .flatMap(edge => pointsToLineSegments(sampleVectorEdgeFromMap(vertexById, edge, adaptiveVectorEditStepsForEdge(vertexById, edge, this.host.camera.zoom))));
    if (selectedVertices.length === 0) {
      const knifeVertices = node.network.edges
        .filter(edge => overlay.knifePreview?.edgeIds.includes(edge.id))
        .flatMap(edge => pointsToLineSegments(sampleVectorEdgeFromMap(vertexById, edge, adaptiveVectorEditStepsForEdge(vertexById, edge, this.host.camera.zoom))));
      if (knifeVertices.length > 0) {
        this.drawSourceOverLines(knifeVertices, [1, 0.18, 0.18, 0.95]);
      }
      return;
    }

    this.drawSourceOverLines(selectedVertices, VECTOR_EDIT_SELECTED_EDGE_COLOR);
    const knifeVertices = node.network.edges
      .filter(edge => overlay.knifePreview?.edgeIds.includes(edge.id))
      .flatMap(edge => pointsToLineSegments(sampleVectorEdgeFromMap(vertexById, edge, adaptiveVectorEditStepsForEdge(vertexById, edge, this.host.camera.zoom))));
    if (knifeVertices.length > 0) {
      this.drawSourceOverLines(knifeVertices, [1, 0.18, 0.18, 0.95]);
    }
  }

  private cachedVectorEditEdges(network: VectorNetwork): number[] {
    const cached = this.host.vectorEditCache.get(network);
    if (cached && cached.edgeZoom === this.host.camera.zoom) {
      return cached.edgeVertices;
    }

    const vertexById = new Map(network.vertices.map(vertex => [vertex.id, vertex]));
    const edgeVertices = network.edges.flatMap(edge => pointsToLineSegments(sampleVectorEdgeFromMap(vertexById, edge, adaptiveVectorEditStepsForEdge(vertexById, edge, this.host.camera.zoom))));
    this.host.vectorEditCache.set(network, { edgeVertices, edgeZoom: this.host.camera.zoom });
    return edgeVertices;
  }

  private drawPrimitiveGizmo(gizmo: PrimitiveGizmoOverlay): void {
    for (const handle of gizmo.handles) {
      this.drawCircleGizmo(handle.x, handle.y, handle.active ? 5 : 4.5, handle.active, handle.hovered);
    }

    if (gizmo.label) {
      this.drawPrimitiveGizmoLabel(gizmo.label);
    }
  }

  private drawPrimitiveGizmoLabel(label: { x: number; y: number; text: string }): void {
    const dpr = Math.max(1, this.host.camera.devicePixelRatio);
    const cacheKey = `pgizmo:${label.text}:${dpr}`;
    let cached = this.host.dimensionLabelTextureCache.get(cacheKey);
    if (!cached) {
      const canvas = this.rasterizeDimensionLabel(label.text);
      const texture = this.host.uploadCanvasTexture(canvas);
      if (!texture) return;
      cached = { texture, width: canvas.width / dpr, height: canvas.height / dpr };
      this.host.dimensionLabelTextureCache.set(cacheKey, cached);
    }
    const offsetPx = 12;
    const labelBounds = {
      x: label.x + offsetPx / this.host.camera.zoom,
      y: label.y - cached.height / this.host.camera.zoom / 2,
      width: cached.width / this.host.camera.zoom,
      height: cached.height / this.host.camera.zoom,
    };
    this.host.drawCachedWebGLTexture(cached.texture, labelBounds);
  }

  private drawGradientGizmo(gizmo: GradientGizmoOverlay): void {
    const gl = this.host.gl;
    const axisBuffer = createTriangleBuffer(gl, [gizmo.start.x, gizmo.start.y, gizmo.end.x, gizmo.end.y]);
    twgl.setBuffersAndAttributes(gl, this.host.programInfo, axisBuffer);
    twgl.setUniforms(this.host.programInfo, this.host.uniforms([0.94, 0.96, 0.98, 0.78]));
    gl.lineWidth(1);
    twgl.drawBufferInfo(gl, axisBuffer, gl.LINES);

    this.drawGradientEndpoint(gizmo.start, gizmo.activeHandle === "start");
    this.drawGradientEndpoint(gizmo.end, gizmo.activeHandle === "end");

    for (const stop of gizmo.stops) {
      this.drawGradientStop(stop, gizmo.activeHandle === stop.id || stop.selected === true);
    }
  }

  private drawImageFillGizmo(gizmo: ImageFillGizmoOverlay): void {
    if (gizmo.corners && gizmo.corners.length >= 4) {
      this.drawSelectionOutlineFromCorners(gizmo.corners, [0.96, 0.98, 1, 0.92]);
    } else {
      this.drawSelectionOutline(gizmo.imageRect, [0.96, 0.98, 1, 0.92]);
    }
    for (const handle of gizmo.handles) {
      this.drawSquareGizmo(handle.x, handle.y, 9, handle.handle === gizmo.activeHandle);
    }
  }

  private drawRotationOriginGizmo(gizmo: RotationOriginGizmoOverlay): void {
    for (const point of gizmo.snapPoints ?? []) {
      this.drawCircleGizmo(point.x, point.y, 4, false, false);
    }

    const size = (gizmo.active || gizmo.hovered ? 16 : 13) / this.host.camera.zoom;
    const half = size / 2;
    const buffer = createTriangleBuffer(this.host.gl, [
      gizmo.x - half, gizmo.y,
      gizmo.x + half, gizmo.y,
      gizmo.x, gizmo.y - half,
      gizmo.x, gizmo.y + half,
    ]);
    twgl.setBuffersAndAttributes(this.host.gl, this.host.programInfo, buffer);
    twgl.setUniforms(this.host.programInfo, this.host.uniforms([0.94, 0.96, 0.98, 0.95]));
    this.host.gl.lineWidth(1);
    twgl.drawBufferInfo(this.host.gl, buffer, this.host.gl.LINES);
    this.drawCircleGizmo(gizmo.x, gizmo.y, gizmo.active || gizmo.hovered ? 6 : 5, gizmo.active, gizmo.hovered);
  }

  private drawGradientEndpoint(point: { x: number; y: number }, selected: boolean): void {
    this.drawVectorGizmo(point.x, point.y, {
      selected,
      radiusPx: selected ? 5 : 4.5,
      color: selected ? [0.125, 0.749, 1, 1] : [0.94, 0.96, 0.98, 1],
    });
  }

  private drawGradientStop(stop: GradientGizmoStopOverlay, selected: boolean): void {
    const connectorBuffer = createTriangleBuffer(this.host.gl, [stop.anchorX, stop.anchorY, stop.x, stop.y]);
    twgl.setBuffersAndAttributes(this.host.gl, this.host.programInfo, connectorBuffer);
    twgl.setUniforms(this.host.programInfo, this.host.uniforms(selected ? [0.125, 0.749, 1, 0.9] : [0.94, 0.96, 0.98, 0.55]));
    this.host.gl.lineWidth(1);
    twgl.drawBufferInfo(this.host.gl, connectorBuffer, this.host.gl.LINES);

    const size = (selected ? 14 : 12) / this.host.camera.zoom;
    const borderSize = size + 4 / this.host.camera.zoom;
    const border = centeredRect(stop.x, stop.y, borderSize);
    const fill = centeredRect(stop.x, stop.y, size);

    const borderBuffer = createTriangleBuffer(this.host.gl, rectVertices(border));
    twgl.setBuffersAndAttributes(this.host.gl, this.host.programInfo, borderBuffer);
    twgl.setUniforms(this.host.programInfo, this.host.uniforms(selected ? [0.125, 0.749, 1, 1] : [0.94, 0.96, 0.98, 0.92]));
    twgl.drawBufferInfo(this.host.gl, borderBuffer);

    const fillBuffer = createTriangleBuffer(this.host.gl, rectVertices(fill));
    twgl.setBuffersAndAttributes(this.host.gl, this.host.programInfo, fillBuffer);
    twgl.setUniforms(this.host.programInfo, this.host.uniforms(stop.color));
    twgl.drawBufferInfo(this.host.gl, fillBuffer);
  }

  private rasterizeFrameLabel(
    name: string,
    cssWidth: number,
    selected: boolean,
    hovered: boolean,
    kind: "frame" | "component",
    hasScript = false,
  ): HTMLCanvasElement {
    const dpr = Math.max(1, this.host.camera.devicePixelRatio);
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.ceil(cssWidth * dpr));
    canvas.height = Math.ceil(FRAME_LABEL_HEIGHT_PX * dpr);
    const context = canvas.getContext("2d");
    if (!context) {
      return canvas;
    }

    context.scale(dpr, dpr);
    context.font = "500 11px Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
    context.textBaseline = "middle";
    const component = kind === "component";
    if (hovered || selected) {
      roundedRectPath(context, { x: 0, y: 0, width: cssWidth, height: FRAME_LABEL_HEIGHT_PX }, [2, 2, 2, 2]);
      context.fillStyle = component
        ? hovered ? "rgba(168, 85, 247, 0.16)" : "rgba(168, 85, 247, 0.1)"
        : hovered ? "rgba(56, 189, 248, 0.14)" : "rgba(56, 189, 248, 0.09)";
      context.fill();
    }

    const scriptPad = hasScript ? FRAME_LABEL_SCRIPT_ACTIONS_WIDTH_PX : 0;
    const componentPad = component ? 14 : 0;
    const textLeft = FRAME_LABEL_PADDING_X_PX + scriptPad + componentPad;
    if (hasScript) {
      const iconColor = component
        ? selected ? "rgba(233, 213, 255, 0.95)" : "rgba(216, 180, 254, 0.9)"
        : selected ? "rgba(186, 230, 253, 0.95)" : "rgba(125, 211, 252, 0.9)";
      const buttonWidth = FRAME_LABEL_SCRIPT_ACTIONS_WIDTH_PX * 0.5;
      drawFrameLabelCodeIcon(context, buttonWidth * 0.5, FRAME_LABEL_HEIGHT_PX * 0.5, iconColor);
      drawFrameLabelPlayIcon(context, buttonWidth * 1.5, FRAME_LABEL_HEIGHT_PX * 0.5, iconColor);
    }
    if (component) {
      const centerY = FRAME_LABEL_HEIGHT_PX / 2;
      const glyphLeft = FRAME_LABEL_PADDING_X_PX + scriptPad;
      context.strokeStyle = selected ? "rgba(233, 213, 255, 1)" : "rgba(216, 180, 254, 1)";
      context.lineWidth = 1.3;
      context.beginPath();
      context.rect(glyphLeft, centerY - 5, 8, 8);
      context.rect(glyphLeft + 3, centerY - 2, 8, 8);
      context.stroke();
    }
    context.fillStyle = component
      ? selected ? "rgba(233, 213, 255, 1)" : "rgba(216, 180, 254, 1)"
      : selected ? "rgba(186, 230, 253, 1)" : "rgba(125, 211, 252, 1)";
    context.fillText(
      ellipsizeText(context, name, cssWidth - textLeft - FRAME_LABEL_PADDING_X_PX),
      textLeft,
      FRAME_LABEL_HEIGHT_PX / 2,
    );
    return canvas;
  }
}

function drawFrameLabelCodeIcon(
  context: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  color: string,
): void {
  context.save();
  context.strokeStyle = color;
  context.lineWidth = 1.35;
  context.lineCap = "round";
  context.lineJoin = "round";
  context.beginPath();
  context.moveTo(cx - 2.2, cy - 3.2);
  context.lineTo(cx - 5.2, cy);
  context.lineTo(cx - 2.2, cy + 3.2);
  context.moveTo(cx + 2.2, cy - 3.2);
  context.lineTo(cx + 5.2, cy);
  context.lineTo(cx + 2.2, cy + 3.2);
  context.stroke();
  context.restore();
}

function drawFrameLabelPlayIcon(
  context: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  color: string,
): void {
  context.save();
  context.fillStyle = color;
  context.beginPath();
  context.moveTo(cx - 3.2, cy - 4);
  context.lineTo(cx + 4.2, cy);
  context.lineTo(cx - 3.2, cy + 4);
  context.closePath();
  context.fill();
  context.restore();
}

function scenePreviewsFromOverlay(overlay: RenderOverlay): ScenePreview[] {
  return overlay.previews
    .filter(preview => preview.nodeId !== "__marquee__")
    .map(preview => ({
      nodeId: preview.nodeId,
      bounds: preview.bounds,
      rotation: preview.rotation,
    }));
}

function dimensionLabelText(bounds: RenderPreview["bounds"]): string | undefined {
  const width = Math.round(Math.abs(bounds.width));
  const height = Math.round(Math.abs(bounds.height));
  if (!Number.isFinite(width) || !Number.isFinite(height)) {
    return undefined;
  }
  return `${width} x ${height}`;
}

function findDisplayOrNodeBounds(
  document: CurvaDocument,
  items: Array<{ id: string; bounds: RenderPreview["bounds"] }>,
  nodeId: string | undefined,
  vectorPreview?: VectorNetworkPreview,
): RenderPreview["bounds"] | undefined {
  if (!nodeId) {
    return undefined;
  }

  if (vectorPreview?.id === nodeId) {
    return vectorPreview.bounds;
  }

  return items.find(item => item.id === nodeId)?.bounds ?? getAllSceneNodes(document).find(node => node.id === nodeId)?.bounds;
}

function findNodeRotation(document: CurvaDocument, overlay: RenderOverlay, nodeId: string | undefined): number {
  if (!nodeId) {
    return 0;
  }

  const preview = overlay.previews.find(candidate => candidate.nodeId === nodeId && candidate.rotation !== undefined);
  if (preview?.rotation !== undefined) {
    return preview.rotation;
  }

  return getAllSceneNodes(document).find(node => node.id === nodeId)?.transform.rotation ?? 0;
}

function findNodeRotationOrigin(
  document: CurvaDocument,
  overlay: RenderOverlay,
  nodeId: string | undefined,
  bounds: RenderPreview["bounds"],
): { x: number; y: number } {
  if (!nodeId) {
    return rectCenter(bounds);
  }

  const node = getAllSceneNodes(document).find(candidate => candidate.id === nodeId);
  if (!node) {
    return rectCenter(bounds);
  }

  const preview = overlay.previews.find(candidate => candidate.nodeId === nodeId);
  if (preview) {
    if (preview.rotation !== undefined && node.transform.rotation === 0) {
      return rectCenter(bounds);
    }
    const origin = transformRotationOrigin(node.bounds, node.transform);
    const transformed = transformDisplayPoint([origin.x, origin.y], node.bounds, preview.bounds);
    return { x: transformed[0], y: transformed[1] };
  }

  return transformRotationOrigin(node.bounds, node.transform);
}

function rotatedBoundsVertices(bounds: RenderPreview["bounds"], rotation: number, origin = rectCenter(bounds)): number[] {
  return [
    { x: bounds.x, y: bounds.y },
    { x: bounds.x + bounds.width, y: bounds.y },
    { x: bounds.x + bounds.width, y: bounds.y + bounds.height },
    { x: bounds.x, y: bounds.y + bounds.height },
  ].flatMap(point => {
    const rotated = rotatePoint(point, origin, rotation);
    return [rotated.x, rotated.y];
  });
}

const EPSILON = 0.001;

function transformRectVertices(bounds: RenderPreview["bounds"], transform: Affine2D): number[] {
  return [
    { x: bounds.x, y: bounds.y },
    { x: bounds.x + bounds.width, y: bounds.y },
    { x: bounds.x, y: bounds.y + bounds.height },
    { x: bounds.x, y: bounds.y + bounds.height },
    { x: bounds.x + bounds.width, y: bounds.y },
    { x: bounds.x + bounds.width, y: bounds.y + bounds.height },
  ].flatMap(point => {
    const transformed = applyAffinePoint(transform, point);
    return [transformed.x, transformed.y];
  });
}
function vectorEditNodeToWorld(
  document: CurvaDocument,
  node: Extract<SceneNode, { type: "vectorNetwork" }>,
  previews: ScenePreview[] = [],
): Extract<SceneNode, { type: "vectorNetwork" }> {
  const matrix = nodeSelectionLocalToWorldMatrix(document, node.id, previews);
  if (affineEquals(matrix, IDENTITY_AFFINE)) {
    return node;
  }

  const toWorld = (point: { x: number; y: number }) => applyAffinePoint(matrix, point);
  const network: VectorNetwork = {
    fillRule: node.network.fillRule,
    fillOverrideTable: node.network.fillOverrideTable ? structuredClone(node.network.fillOverrideTable) : undefined,
    regions: node.network.regions.map(region => ({ ...region, edgeIds: [...region.edgeIds] })),
    vertices: node.network.vertices.map(vertex => ({ ...vertex, ...toWorld(vertex) })),
    edges: node.network.edges.map(edge => ({
      ...edge,
      from: {
        vertexId: edge.from.vertexId,
        handle: edge.from.handle ? toWorld(edge.from.handle) : undefined,
      },
      to: {
        vertexId: edge.to.vertexId,
        handle: edge.to.handle ? toWorld(edge.to.handle) : undefined,
      },
    })),
  };

  return {
    ...node,
    transform: { ...node.transform, rotation: 0 },
    network,
    bounds: getVectorNetworkBounds(network),
  };
}

function vectorEditOverlayToWorld(
  document: CurvaDocument,
  overlay: RenderOverlay,
  node: Extract<SceneNode, { type: "vectorNetwork" }>,
  previews: ScenePreview[] = [],
): RenderOverlay {
  const matrix = nodeSelectionLocalToWorldMatrix(document, node.id, previews);
  if (affineEquals(matrix, IDENTITY_AFFINE)) {
    return overlay;
  }

  const toWorld = (point: { x: number; y: number }) => applyAffinePoint(matrix, point);
  return {
    ...overlay,
    vertexPreview: overlay.vertexPreview?.nodeId === node.id
      ? {
          ...overlay.vertexPreview,
          ...toWorld(overlay.vertexPreview),
          vertices: overlay.vertexPreview.vertices?.map(vertex => ({ ...vertex, ...toWorld(vertex) })),
          handles: overlay.vertexPreview.handles?.map(handle => ({ ...handle, ...toWorld(handle) })),
        }
      : overlay.vertexPreview,
    handlePreview: overlay.handlePreview?.nodeId === node.id
      ? {
          ...overlay.handlePreview,
          ...toWorld(overlay.handlePreview),
          mirroredHandle: overlay.handlePreview.mirroredHandle
            ? { ...overlay.handlePreview.mirroredHandle, ...toWorld(overlay.handlePreview.mirroredHandle) }
            : undefined,
        }
      : overlay.handlePreview,
    edgeBendPreview: overlay.edgeBendPreview?.nodeId === node.id
      ? {
          ...overlay.edgeBendPreview,
          fromHandle: toWorld(overlay.edgeBendPreview.fromHandle),
          toHandle: toWorld(overlay.edgeBendPreview.toHandle),
        }
      : overlay.edgeBendPreview,
    sculptBrushPreview: overlay.sculptBrushPreview?.nodeId === node.id
      ? { ...overlay.sculptBrushPreview, ...toWorld(overlay.sculptBrushPreview) }
      : overlay.sculptBrushPreview,
    pathPreviewPoint: overlay.pathPreviewPoint?.nodeId === node.id
      ? { ...overlay.pathPreviewPoint, ...toWorld(overlay.pathPreviewPoint) }
      : overlay.pathPreviewPoint,
    pathPreviewSegment: overlay.pathPreviewSegment?.nodeId === node.id
      ? {
          ...overlay.pathPreviewSegment,
          from: toWorld(overlay.pathPreviewSegment.from),
          to: toWorld(overlay.pathPreviewSegment.to),
        }
      : overlay.pathPreviewSegment,
    knifePreview: overlay.knifePreview
      ? { ...overlay.knifePreview, points: overlay.knifePreview.points.map(toWorld) }
      : undefined,
    shapeBuilderRegions: overlay.shapeBuilderRegions?.map(region => ({
      ...region,
      contour: region.contour.map(toWorld),
    })),
  };
}

function previewHandleFor(
  overlay: RenderOverlay,
  edgeId: string,
  endpoint: "from" | "to",
): { x: number; y: number } | undefined {
  if (overlay.handlePreview?.edgeId === edgeId && overlay.handlePreview.endpoint === endpoint) {
    return { x: overlay.handlePreview.x, y: overlay.handlePreview.y };
  }

  if (overlay.handlePreview?.mirroredHandle?.edgeId === edgeId && overlay.handlePreview.mirroredHandle.endpoint === endpoint) {
    return { x: overlay.handlePreview.mirroredHandle.x, y: overlay.handlePreview.mirroredHandle.y };
  }

  return undefined;
}

function measureLineVertices(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  dashLength?: number,
  gapLength?: number,
): number[] {
  if (!dashLength || !gapLength) {
    return [x1, y1, x2, y2];
  }

  const dx = x2 - x1;
  const dy = y2 - y1;
  const length = Math.hypot(dx, dy);
  if (length <= EPSILON) {
    return [];
  }

  const unitX = dx / length;
  const unitY = dy / length;
  const vertices: number[] = [];
  let traveled = 0;
  let drawing = true;

  while (traveled < length) {
    const segmentLength = Math.min(drawing ? dashLength : gapLength, length - traveled);
    const startX = x1 + unitX * traveled;
    const startY = y1 + unitY * traveled;
    const endX = x1 + unitX * (traveled + segmentLength);
    const endY = y1 + unitY * (traveled + segmentLength);
    if (drawing) {
      vertices.push(startX, startY, endX, endY);
    }
    traveled += segmentLength;
    drawing = !drawing;
  }

  return vertices;
}

function thickLineVertices(
  from: { x: number; y: number },
  to: { x: number; y: number },
  width: number,
): number[] {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const length = Math.hypot(dx, dy);
  if (length <= EPSILON) {
    return [];
  }

  const nx = (-dy / length) * width / 2;
  const ny = (dx / length) * width / 2;
  return [
    from.x + nx,
    from.y + ny,
    to.x + nx,
    to.y + ny,
    from.x - nx,
    from.y - ny,
    from.x - nx,
    from.y - ny,
    to.x + nx,
    to.y + ny,
    to.x - nx,
    to.y - ny,
  ];
}

function rectsIntersect(left: RenderPreview["bounds"], right: RenderPreview["bounds"]): boolean {
  return left.x < right.x + right.width &&
    left.x + left.width > right.x &&
    left.y < right.y + right.height &&
    left.y + left.height > right.y;
}
